export type QuestionTask = {
  id: string;
  practiceNumber: number;
  intervalDays: number;
  dueDate: string;
  targetQuestions: number;
  solvedQuestions: number;
  status: "pending" | "completed";
  completedAt: string | null;
  attemptedQuestions: number;
  correctQuestions: number;
  incorrectQuestions: number;
  rescheduled: boolean;
};

export type QuestionChapter = {
  id: string;
  subject: string;
  chapter: string;
  topic: string | null;
  source: string;
  scheduleMode: "auto" | "self";
  selfStartDate: string | null;
  selfGapDays: number | null;
  selfPerSession: number | null;
  selfEndDate: string | null;
  targetQuestions: number;
  solvedQuestions: number;
  createdAt: string;
  tasks: QuestionTask[];
};

/** Day offset -> questions. Totals 180. */
export const QUESTION_PLAN = [
  { day: 1, questions: 20 },
  { day: 3, questions: 20 },
  { day: 7, questions: 30 },
  { day: 15, questions: 30 },
  { day: 30, questions: 40 },
  { day: 60, questions: 40 },
] as const;

export const DEFAULT_QUESTION_TARGET = QUESTION_PLAN.reduce((s, p) => s + p.questions, 0);

export const QUESTION_SUBJECTS = [
  { key: "Physics", label: "Physics", emoji: "⚡" },
  { key: "Chemistry", label: "Chemistry", emoji: "🧪" },
  { key: "Maths", label: "Mathematics", emoji: "📐" },
  { key: "Biology", label: "Biology", emoji: "🧬" },
  { key: "Other", label: "Others", emoji: "📚" },
] as const;

export function subjectMeta(subject: string) {
  return (
    QUESTION_SUBJECTS.find((s) => s.key === subject) ?? {
      key: subject,
      label: subject,
      emoji: "📚",
    }
  );
}

export type TaskState = "locked" | "today" | "overdue" | "completed";

export function daysBetween(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00`).getTime();
  const b = new Date(`${to}T00:00:00`).getTime();
  return Math.round((b - a) / 86400000);
}

export function taskState(task: QuestionTask, today: string): TaskState {
  if (task.status === "completed") return "completed";
  if (task.dueDate === today) return "today";
  return task.dueDate < today ? "overdue" : "locked";
}

/**
 * Missed practice never disappears: any overdue, still-pending session rolls its
 * questions forward onto the next upcoming session's displayed target.
 */
export function effectiveTarget(tasks: QuestionTask[], task: QuestionTask, today: string): number {
  const state = taskState(task, today);
  if (state !== "today" && state !== "locked") return task.targetQuestions;

  const sorted = [...tasks].sort((a, b) => a.practiceNumber - b.practiceNumber);
  const upcoming = sorted.find((t) => t.status === "pending" && t.dueDate >= today);
  if (!upcoming || upcoming.id !== task.id) return task.targetQuestions;

  const carried = sorted
    .filter((t) => t.status === "pending" && t.dueDate < today)
    .reduce((sum, t) => sum + t.targetQuestions, 0);
  return task.targetQuestions + carried;
}

export type Remark = {
  label: string;
  emoji: string;
  tone: "excellent" | "good" | "improve" | "critical";
};

export function remarkFor(task: QuestionTask): Remark | null {
  if (task.status !== "completed" || !task.completedAt) return null;
  const done = task.completedAt.slice(0, 10);
  const late = Math.max(0, daysBetween(task.dueDate, done));
  if (late === 0) return { label: "Excellent", emoji: "🟢", tone: "excellent" };
  if (late <= 3) return { label: "Good", emoji: "🟡", tone: "good" };
  if (late <= 7) return { label: "Needs Improvement", emoji: "🟠", tone: "improve" };
  return { label: "Critical", emoji: "🔴", tone: "critical" };
}

export function remarkClasses(tone: Remark["tone"]): string {
  switch (tone) {
    case "excellent":
      return "border-success/25 bg-success/12 text-success";
    case "good":
      return "border-warning/25 bg-warning/12 text-warning";
    case "improve":
      return "border-orange-500/25 bg-orange-500/12 text-orange-400";
    default:
      return "border-destructive/25 bg-destructive/12 text-destructive";
  }
}

export type QuestionHealth = {
  percent: number;
  label: string;
  emoji: string;
  tone: "strong" | "average" | "weak";
};

/** Health = how much of the questions that were *due so far* are actually solved. */
export function chapterHealth(chapter: QuestionChapter, today: string): QuestionHealth {
  const due = chapter.tasks
    .filter((t) => t.dueDate <= today)
    .reduce((sum, t) => sum + t.targetQuestions, 0);
  const solved = chapter.tasks.reduce((sum, t) => sum + t.solvedQuestions, 0);
  const percent = due === 0 ? 100 : Math.max(0, Math.min(100, Math.round((solved / due) * 100)));
  if (percent >= 90) return { percent, label: "Strong", emoji: "🟢", tone: "strong" };
  if (percent >= 60) return { percent, label: "Average", emoji: "🟡", tone: "average" };
  return { percent, label: "Weak", emoji: "🔴", tone: "weak" };
}

export function healthClasses(tone: QuestionHealth["tone"]): string {
  switch (tone) {
    case "strong":
      return "border-success/25 bg-success/12 text-success";
    case "average":
      return "border-warning/25 bg-warning/12 text-warning";
    default:
      return "border-destructive/25 bg-destructive/12 text-destructive";
  }
}

export function chapterProgress(chapter: QuestionChapter): {
  solved: number;
  remaining: number;
  percent: number;
} {
  const solved = Math.min(chapter.solvedQuestions, chapter.targetQuestions);
  return {
    solved,
    remaining: Math.max(0, chapter.targetQuestions - solved),
    percent: chapter.targetQuestions
      ? Math.round((solved / chapter.targetQuestions) * 100)
      : 0,
  };
}

export function nextPracticeDate(chapter: QuestionChapter, today: string): string | null {
  const next = [...chapter.tasks]
    .filter((t) => t.status === "pending")
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];
  return next ? next.dueDate : null;
}

export function practiceStreak(chapters: QuestionChapter[], today: string): number {
  const days = new Set<string>();
  for (const c of chapters) {
    for (const t of c.tasks) {
      if (t.status === "completed" && t.completedAt) days.add(t.completedAt.slice(0, 10));
    }
  }
  let streak = 0;
  const cursor = new Date(`${today}T00:00:00`);
  if (!days.has(today)) cursor.setDate(cursor.getDate() - 1);
  for (;;) {
    const key = cursor.toISOString().slice(0, 10);
    const local = `${cursor.getFullYear()}-${`${cursor.getMonth() + 1}`.padStart(2, "0")}-${`${cursor.getDate()}`.padStart(2, "0")}`;
    if (!days.has(local) && !days.has(key)) break;
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}
/* ------------------------------------------------------------------ *
 * Question sources & self-scheduling
 * ------------------------------------------------------------------ */

export const QUESTION_SOURCES = [
  "PYQ",
  "Module",
  "NCERT",
  "Coaching Sheet",
  "Mock/Test",
  "Practice",
  "Other",
] as const;

export type QuestionSource = (typeof QUESTION_SOURCES)[number];

export const SCHEDULE_GAPS = [
  { days: 1, label: "Every day" },
  { days: 2, label: "Every 2 days" },
  { days: 3, label: "Every 3 days" },
  { days: 4, label: "Every 4 days" },
] as const;

export function addDaysTo(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export type SelfScheduleInput = {
  total: number;
  startDate: string;
  gapDays: number;
  perSession: number;
  endDate?: string | null;
};

export type SelfScheduleSession = { index: number; date: string; questions: number };

export type SelfSchedulePreview = {
  sessions: SelfScheduleSession[];
  sessionCount: number;
  completionDate: string | null;
  warning: string | null;
};

/**
 * Pure, student-controlled plan: fixed questions per session, fixed gap, and a
 * final short session for whatever is left over.
 */
export function buildSelfSchedule(input: SelfScheduleInput): SelfSchedulePreview {
  const total = Math.max(1, Math.floor(input.total));
  const per = Math.max(1, Math.floor(input.perSession));
  const gap = Math.max(1, Math.floor(input.gapDays));
  const count = Math.ceil(total / per);
  const sessions: SelfScheduleSession[] = [];
  let remaining = total;
  for (let i = 0; i < count; i += 1) {
    const questions = Math.min(per, remaining);
    remaining -= questions;
    sessions.push({ index: i + 1, date: addDaysTo(input.startDate, i * gap), questions });
  }
  const completionDate = sessions.length ? sessions[sessions.length - 1].date : null;
  let warning: string | null = null;
  if (input.endDate && completionDate && completionDate > input.endDate) {
    const daysAvailable = daysBetween(input.startDate, input.endDate);
    const possibleSessions = Math.floor(daysAvailable / gap) + 1;
    const needPerSession = Math.ceil(total / Math.max(1, possibleSessions));
    warning =
      `This plan finishes on ${completionDate}, after your end date. ` +
      `Either raise questions per session to about ${needPerSession}, choose a smaller gap, or move the end date.`;
  }
  return { sessions, sessionCount: sessions.length, completionDate, warning };
}

export type QuestionStats = {
  attempted: number;
  correct: number;
  incorrect: number;
  unattempted: number;
  accuracy: number;
};

/** Question-only stats. Nothing here depends on lectures. */
export function questionStats(chapter: QuestionChapter): QuestionStats {
  let attempted = 0;
  let correct = 0;
  let incorrect = 0;
  for (const t of chapter.tasks) {
    attempted += t.attemptedQuestions || 0;
    correct += t.correctQuestions || 0;
    incorrect += t.incorrectQuestions || 0;
  }
  return {
    attempted,
    correct,
    incorrect,
    unattempted: Math.max(0, chapter.targetQuestions - attempted),
    accuracy: attempted ? Math.round((correct / attempted) * 100) : 0,
  };
}

/** Auto sets carry missed work forward; self sets stay exactly as the student planned. */
export function plannedTarget(
  chapter: QuestionChapter,
  task: QuestionTask,
  today: string,
): number {
  return chapter.scheduleMode === "self"
    ? task.targetQuestions
    : effectiveTarget(chapter.tasks, task, today);
}
